/**
 * Stars — render a 0–5 rating (fractional supported) as gold star glyphs.
 *
 * Read-only display for the catalog/reviews. Ratings come from the server
 * (``rating_avg``); this component only visualizes them.
 */
import { cn } from "../utils/cn";
import styles from "./Stars.module.css";

export interface StarsProps {
  /** Rating in the inclusive range [0, max]. Clamped on render. */
  readonly value: number;
  /** Maximum number of stars. Defaults to ``5``. */
  readonly max?: number;
  readonly size?: "sm" | "md" | "lg";
  /** Append the numeric value (e.g. "4.7") after the stars. Defaults to ``false``. */
  readonly showValue?: boolean;
  /** Optional review count rendered as "(123)". */
  readonly count?: number;
  readonly className?: string;
}

export function Stars({
  value,
  max = 5,
  size = "md",
  showValue = false,
  count,
  className,
}: StarsProps): JSX.Element {
  const clamped = Math.max(0, Math.min(value, max));
  const fillPercent = (clamped / max) * 100;

  return (
    <span
      className={cn(styles.stars, styles[size], className)}
      role="img"
      aria-label={`Рейтинг ${clamped.toFixed(1)} из ${max}`}
    >
      <span className={styles.track} aria-hidden="true">
        {"★".repeat(max)}
        <span
          className={styles.fill}
          style={{ width: `${fillPercent}%` }}
        >
          {"★".repeat(max)}
        </span>
      </span>
      {showValue ? (
        <span className={styles.value}>{clamped.toFixed(1)}</span>
      ) : null}
      {count !== undefined ? (
        <span className={styles.count}>({count})</span>
      ) : null}
    </span>
  );
}
